import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFydUser } from '@/store/auth';
import { useUser } from '@clerk/clerk-react';
import { ApiError } from '@/api/client';
import { cancelSubscription, getBillingPortal } from '@/api/billing';
import PageHeader from '@/ui/organisms/PageHeader';
import Card from '@/ui/atoms/Card';
import Button from '@/ui/atoms/Button';
import Eyebrow from '@/ui/atoms/Eyebrow';
import Field from '@/ui/atoms/Field';
import LoadingState from '@/ui/molecules/LoadingState';
import ErrorState from '@/ui/molecules/ErrorState';
import EmptyState from '@/ui/molecules/EmptyState';
import ConfirmDialog from '@/ui/molecules/ConfirmDialog';
import SettingsPanel from '@/components/SettingsPanel';

const TABS = ['profile', 'billing', 'data', 'delete'] as const;
type Tab = (typeof TABS)[number];

// PR-UX-04 4 states per tab.
// PR-UX-10 confirm-undo · cancel subscription + delete account both gated by ConfirmDialog.
// PR-UX-05 JTBD · 4 tabs map to 4 distinct user intents (manage identity / billing / export / delete).
export default function Account() {
  const { tab } = useParams();
  const { t } = useTranslation();
  const active = (tab && TABS.includes(tab as Tab) ? tab : 'profile') as Tab;

  return (
    <div className="max-w-2xl mx-auto px-8 py-12">
      <PageHeader title={t('account.title')} eyebrow={t('account.eyebrow')} />

      <nav className="flex gap-1 border-b border-hairline mb-10" role="tablist">
        {TABS.map((id) => (
          <Link
            key={id}
            to={`/account/${id}`}
            role="tab"
            aria-selected={active === id}
            className={
              'py-3 px-4 text-sm font-medium border-b-2 transition ' +
              (active === id
                ? 'text-ink border-ink'
                : 'text-ink-3 hover:text-ink-2 border-transparent')
            }
          >
            {t(`account.tab.${id}`)}
          </Link>
        ))}
      </nav>

      {active === 'profile' && <ProfileTab />}
      {active === 'billing' && <BillingTab />}
      {active === 'data' && <DataTab />}
      {active === 'delete' && <DeleteTab />}
    </div>
  );
}

// ───── Profile ─────
function ProfileTab() {
  const { t } = useTranslation();
  const me = useFydUser((s) => s.me);
  const { user, isLoaded } = useUser();

  if (!isLoaded) return <LoadingState label={t('account.loading')} />;
  if (!user) return <ErrorState title={t('account.errorTitle')} message={t('account.notSignedIn')} />;

  return (
    <Card padding="loose">
      <Eyebrow tone="ash" className="mb-3">{t('account.profile.identity')}</Eyebrow>
      <dl className="space-y-3 text-base">
        <Row label={t('account.profile.email')} value={user.primaryEmailAddress?.emailAddress ?? '—'} />
        <Row label={t('account.profile.created')} value={me ? new Date(me.created_at).toLocaleDateString() : '—'} />
        <Row
          label={t('account.profile.councils')}
          value={String(me?.councils_run ?? 0)}
        />
      </dl>
      <p className="text-sm text-ink-3 mt-6">{t('account.profile.identityHint')}</p>
      <div className="mt-8">
        <SettingsPanel />
      </div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-hairline pb-3 last:border-0 last:pb-0">
      <dt className="font-mono text-[11px] tracking-meta uppercase text-ink-3">{label}</dt>
      <dd className="text-[15px] text-right">{value}</dd>
    </div>
  );
}

// ───── Billing ─────
function BillingTab() {
  const { t } = useTranslation();
  const me = useFydUser((s) => s.me);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  if (!me) return <LoadingState label={t('account.loading')} />;

  const status = me.subscription_status;
  const onCancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await cancelSubscription();
      setInfo(t('account.billing.cancelled', { date: new Date(r.current_period_end).toLocaleDateString() }));
      setConfirmCancel(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  const onManagePayment = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await getBillingPortal();
      window.open(r.portal_url, '_blank', 'noopener');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Portal unavailable');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card padding="loose">
      <Eyebrow tone="ash" className="mb-3">{t('account.billing.heading')}</Eyebrow>
      <dl className="space-y-3 text-base">
        <Row label={t('account.billing.status')} value={t(`dash.status.${status}`)} />
        <Row label={t('account.billing.trialUsed')} value={me.trial_used ? t('common.yes') : t('common.no')} />
      </dl>

      {error && <p className="text-sm text-vermilion-700 mt-4">{error}</p>}
      {info && <p className="text-sm text-azure-700 mt-4">{info}</p>}

      <div className="flex gap-3 mt-6 flex-wrap">
        {(status === 'active' || status === 'past_due') && (
          <Button variant="secondary" onClick={onManagePayment} loading={busy}>
            {t('account.billing.managePayment')}
          </Button>
        )}
        {status === 'active' && (
          <Button variant="ghost" onClick={() => setConfirmCancel(true)} disabled={busy}>
            {t('account.billing.cancel')}
          </Button>
        )}
        {status === 'inactive' && (
          <Link to="/pricing" className="btn btn-primary">
            {t('account.billing.subscribe')}
          </Link>
        )}
      </div>

      <ConfirmDialog
        open={confirmCancel}
        title={t('confirm.cancel.title')}
        body={t('confirm.cancel.body')}
        confirmLabel={t('confirm.cancel.confirm')}
        cancelLabel={t('confirm.cancel.cancel')}
        destructive
        onConfirm={onCancel}
        onCancel={() => setConfirmCancel(false)}
        busy={busy}
      />
    </Card>
  );
}

// ───── Data export ─────
function DataTab() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onExport = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/me/data-export', { credentials: 'include' });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fyd-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card padding="loose">
      <Eyebrow tone="ash" className="mb-3">{t('account.data.heading')}</Eyebrow>
      <p className="text-base text-ink-2 mb-6">{t('account.data.body')}</p>
      <Button variant="primary" onClick={onExport} loading={busy}>
        {t('account.data.download')}
      </Button>
      {error && <p className="text-sm text-vermilion-700 mt-4">{error}</p>}
    </Card>
  );
}

// ───── Delete account ─────
function DeleteTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/me/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirm: 'DELETE' }),
      });
      if (!res.ok) {
        const body = (await res.json()) as { message?: string };
        throw new Error(body.message ?? `Status ${res.status}`);
      }
      navigate('/');
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      setError(apiErr?.message ?? (err instanceof Error ? err.message : 'Delete failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <EmptyState
        eyebrow={t('account.delete.eyebrow')}
        title={t('account.delete.title')}
        sub={t('account.delete.body')}
      />
      <Card padding="normal" className="border-vermilion/40">
        <Field
          label={t('account.delete.typeToConfirm')}
          value={confirm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirm(e.target.value)}
          placeholder="DELETE"
          className="mb-4"
        />
        <Button
          variant="vermilion"
          onClick={() => setShowConfirm(true)}
          disabled={confirm !== 'DELETE'}
        >
          {t('account.delete.button')}
        </Button>
        {error && <p className="text-sm text-vermilion-700 mt-4">{error}</p>}
      </Card>

      <ConfirmDialog
        open={showConfirm}
        title={t('confirm.delete.title')}
        body={t('confirm.delete.body')}
        confirmLabel={t('confirm.delete.confirm')}
        cancelLabel={t('confirm.delete.cancel')}
        destructive
        onConfirm={onDelete}
        onCancel={() => setShowConfirm(false)}
        busy={busy}
      />
    </>
  );
}
